import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  IndianRupee,
  Leaf,
  Search,
  ShieldCheck,
  Ticket,
} from "lucide-react";
import { ROUTE_PATHS } from "@/routes/routePaths";

const STATS = [
  { icon: IndianRupee, label: "Save 50%", caption: "vs cabs" },
  { icon: Clock, label: "Direct", caption: "no detours" },
  { icon: ShieldCheck, label: "Verified", caption: "hosts" },
] as const;

const LOOKUPS = [
  { title: "Look up a Ride by ID", icon: Search, to: ROUTE_PATHS.RIDE_INFO },
  { title: "Look up a Booking by ID", icon: Ticket, to: ROUTE_PATHS.BOOKING_INFO },
] as const;

export default function TrackRide() {
  const navigate = useNavigate();

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Track a Ride — Duri Ridesharing";
    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <div className="min-h-[80vh] bg-muted/30 px-4 py-12 sm:py-16">
      <div className="mx-auto max-w-xl">
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
            <Leaf className="h-3.5 w-3.5" />
            Greener commutes
          </span>
          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            Share the ride. <span className="text-emerald-600">Save the planet.</span>
          </h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">
            Trusted carpool rides for daily commuters. Save money, save time,
            breathe cleaner air.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-3">
          {STATS.map(({ icon: Icon, label, caption }) => (
            <div
              key={label}
              className="rounded-xl border border-border/70 bg-card px-4 py-4 text-center shadow-sm"
            >
              <Icon className="mx-auto h-5 w-5 text-emerald-600" />
              <p className="mt-2 text-sm font-bold text-foreground">{label}</p>
              <p className="text-xs text-muted-foreground">{caption}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {LOOKUPS.map(({ title, icon: Icon, to }) => (
            <button
              key={to}
              type="button"
              onClick={() => navigate(to)}
              className="group flex w-full items-center justify-between rounded-2xl border border-border/70 bg-card px-5 py-4 text-left shadow-sm transition-colors hover:border-emerald-300"
            >
              <span className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-base font-bold text-foreground">{title}</span>
              </span>
              <ArrowRight className="h-5 w-5 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-emerald-600" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
